// @ts-nocheck
import { useState } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, InputNumber, Switch,
  message, ColorPicker, Dropdown,
} from "../components/admin-ui";
import type { Color } from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined,
  CheckCircleOutlined, CloseCircleOutlined, MoreOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { PromoBanner } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { tokens } from "../styles/tokens";
const { color: T } = tokens;
import { AdminShell } from "../layouts/AdminShell";
import { ImageUpload } from "../components/ImageUpload";
import { useAdminBanners, useCreateBanner, useUpdateBanner, useDeleteBanner } from "../api/hooks";

function BannerStatCard({ label, value, icon, iconColor, badge, badgeColor, badgeBg }: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-banner-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-banner-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

export function BannersScreen() {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromoBanner | null>(null);
  const [form] = Form.useForm();

  const previewTitle = Form.useWatch("title", form);
  const previewTag = Form.useWatch("tag", form);
  const previewTagColor = Form.useWatch("tag_color", form);
  const previewImageUrl = Form.useWatch("image_url", form);

  const { data, isLoading } = useAdminBanners();
  const createBanner = useCreateBanner();
  const updateBanner = useUpdateBanner();
  const deleteBanner = useDeleteBanner();

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (b: PromoBanner) => {
    setEditing(b);
    form.setFieldsValue(b);
    setModalOpen(true);
  };

  const onSave = async (values: Record<string, unknown>) => {
    try {
      if (editing) {
        await updateBanner.mutateAsync({ id: editing.id, ...values });
        void message.success(t("banners.updateSuccess"));
      } else {
        await createBanner.mutateAsync(values);
        void message.success(t("banners.createSuccess"));
      }
      setModalOpen(false);
    } catch {
      void message.error(t("banners.saveFailed"));
    }
  };

  const onDelete = async (row: PromoBanner) => {
    try {
      await deleteBanner.mutateAsync(row.id);
      void message.success(t("banners.deleteSuccess"));
    } catch {
      void message.error(t("banners.deleteFailed"));
    }
  };

  const confirmDelete = (row: PromoBanner) => {
    Modal.confirm({
      title: t("banners.deleteConfirm"),
      okText: t("common.yes"),
      cancelText: t("common.no"),
      danger: true,
      onOk: () => onDelete(row),
    });
  };

  const columns: ColumnsType<PromoBanner> = [
    {
      title: t("banners.colOrder"),
      dataIndex: "sort_order",
      key: "sort_order",
      width: 80,
      render: (value: number) => <span className="admin-data-amount">{value}</span>,
    },
    {
      title: t("banners.colTag"),
      dataIndex: "tag",
      key: "tag",
      width: 140,
      render: (tag: string, row: PromoBanner) => (
        <span className="admin-data-badge admin-banner-tag"
          style={{
            background: row.tag_color,
            color: T.surface,
          }}
        >
          {tag}
        </span>
      ),
    },
    {
      title: t("banners.colTitle"),
      dataIndex: "title",
      key: "title",
      render: (value: string) => <span className="admin-data-customer">{value}</span>,
    },
    {
      title: t("banners.colPreview"),
      key: "preview",
      width: 120,
      render: (_: unknown, row: PromoBanner) => (
        <div className="admin-banner-table-preview" style={{ background: row.bg_gradient }}>
          {row.image_url && (
            <img src={row.image_url} alt="" />
          )}
        </div>
      ),
    },
    {
      title: t("banners.colActive"),
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.successText : T.textSecondary, background: v ? T.successBg : T.surfaceSection }}>
          {v ? t("common.yes") : t("common.no")}
        </span>
      ),
    },
    {
      title: t("banners.colActions"),
      key: "actions",
      width: 96,
      fixed: "right",
      render: (_, row) => (
        <Dropdown
          menu={{ items: [
            { key: "edit", icon: <EditOutlined />, label: t("common.edit"), onClick: () => openEdit(row) },
            { key: "delete", icon: <DeleteOutlined />, label: t("common.delete"), danger: true, onClick: () => confirmDelete(row) },
          ] }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button className="admin-banner-more admin-data-more" type="text"
            aria-label={`${t("banners.colActions")} ${row.title}`} icon={<MoreOutlined style={{ fontSize: 18 }} />} />
        </Dropdown>
      ),
    },
  ];

  const banners = (data as { data: PromoBanner[] } | undefined)?.data ?? [];
  const activeCount = banners.filter(banner => banner.is_active).length;
  const inactiveCount = banners.length - activeCount;

  return (
    <AdminShell>
      <div className="admin-banners-page admin-list-page">
        <div className="admin-banners-page-inner">
          <div className="admin-banners-toolbar">
            <div>
              <h1 className="admin-page-title">{t("banners.title")}</h1>
              <p className="admin-banners-subtitle">{t("banners.pageSubtitle")}</p>
            </div>
            <Button className="admin-banners-create" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("banners.add")}
            </Button>
          </div>

          <section className="admin-banners-stats admin-txn-stats grid">
            <BannerStatCard label={t("banners.statTotal")} value={banners.length} icon={<PictureOutlined />} iconColor={T.brand} badge={t("banners.statBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg} />
            <BannerStatCard label={t("banners.statActive")} value={activeCount} icon={<CheckCircleOutlined />} iconColor={T.successText} badge={t("banners.statBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg} />
            <BannerStatCard label={t("banners.statInactive")} value={inactiveCount} icon={<CloseCircleOutlined />} iconColor={T.textSecondary} badge={t("banners.statBadgeInactive")} badgeColor={T.textSecondary} badgeBg={T.surfaceSection} />
          </section>

          <p className="admin-banners-results">{t("common.total", { count: banners.length })}</p>
          <div className="admin-banners-table-panel admin-data-table-panel">
            <DataTable<PromoBanner>
              columns={columns}
              dataSource={banners}
              rowKey="id"
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={<span className="admin-banner-dialog-title"><PictureOutlined />{editing ? t("banners.editTitle") : t("banners.createTitle")}</span>}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createBanner.isPending || updateBanner.isPending}
        destroyOnHidden
      >
        <Form className="admin-banner-form" form={form} layout="vertical" colon={false} onFinish={onSave}>
          <div className="admin-banner-section-label">{t("banners.sectionContent")}</div>
          <div className="admin-banner-form-fields">
          <Form.Item
            name="title"
            label={t("banners.fieldTitle")}
            rules={[{ required: true, message: t("banners.titleRequired") }]}
          >
            <Input placeholder={t("banners.titlePlaceholder")} />
          </Form.Item>

          <Form.Item
            name="tag"
            label={t("banners.fieldTag")}
            rules={[{ required: true, message: t("banners.tagRequired") }]}
          >
            <Input placeholder={t("banners.tagPlaceholder")} />
          </Form.Item>
          </div>

          <div className="admin-banner-section-label">{t("banners.sectionAppearance")}</div>
          <Form.Item
            className="admin-banner-color-field"
            name="tag_color"
            label={t("banners.fieldTagColor")}
            initialValue="#9c3e00"
            // Normalize: store as hex string, not AntD Color object
            getValueFromEvent={(color: Color) => color.toHexString()}
            getValueProps={(v: string) => ({ value: v })}
          >
            <ColorPicker
              showText
              format="hex"
              presets={[
                {
                  label: t("banners.colorPresets"),
                  colors: [
                    "#9c3e00", "#FF4D4F", "#FF7A31", "#FAAD14",
                    "#52C41A", "#13C2C2", "#1677FF", "#722ED1",
                    "#EB2F96", "#000000", "#FFFFFF",
                  ],
                },
              ]}
            />
          </Form.Item>

          <Form.Item className="admin-banner-image-field" name="image_url" label={t("banners.fieldImageUrl")}>
            <ImageUpload folder="banners" />
          </Form.Item>

          {/* Live banner preview */}
          {(previewTitle || previewTag || previewImageUrl) && (
            <div className="admin-banner-live-preview">
              <div className="admin-banner-preview-label">{t("banners.preview")}</div>
              <div className="admin-banner-preview-canvas">
                {previewImageUrl && (
                  <img
                    src={previewImageUrl}
                    alt={t("banners.previewImageAlt")}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
                <div style={{
                  position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                  justifyContent: "flex-end", padding: 20,
                  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)",
                }}>
                  {previewTag && (
                    <div style={{
                      background: previewTagColor || "#9c3e00", borderRadius: 999,
                      alignSelf: "flex-start", padding: "3px 10px", marginBottom: 8,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.surface, letterSpacing: 1, textTransform: "uppercase" }}>
                        {previewTag}
                      </span>
                    </div>
                  )}
                  {previewTitle && (
                    <div style={{ fontSize: 20, fontWeight: 700, color: T.surface, lineHeight: 1.25 }}>
                      {previewTitle}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="admin-banner-section-label">{t("banners.sectionPublishing")}</div>
          <div className="admin-banner-form-fields admin-banner-form-fields--publishing">
          <Form.Item
            name="sort_order"
            label={t("banners.fieldOrder")}
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            className="admin-banner-active-field"
            name="is_active"
            label={t("banners.fieldActive")}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          </div>
        </Form>
      </Modal>
    </AdminShell>
  );
}
